import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'; // Modulo de Angular http
import { BehaviorSubject, catchError, firstValueFrom, Observable, of, retry, throwError, timer } from 'rxjs';
import { Geolocation } from '@capacitor/geolocation';
import { IS_NATIVE } from '../shared/platform.util';
import { Usuario } from '../models/Usuario';
import { Cliente } from '../models/Cliente';
import { Prestador } from '../models/Prestador';
import { Comercio } from '../models/Comercio';
import { ObraSocial } from '../models/ObraSocial';
import { TipoDocumento } from '../models/TipoDocumento';
import { Ubicacion } from '../models/Ubicacion';
import { Horario } from '../models/Horario';
import { Producto } from '../models/Producto';
import { Servicio } from '../models/Servicio';
import { Especialidad } from '../models/Especialidad';
import { Prestacion } from '../models/Prestacion';
import { Perfil } from '../models/Perfil';
import { API_KEY_GOOGLE_MAPS, API_URI } from 'src/environments/environment';
import { LoadingService } from './loading.service';

@Injectable({
  providedIn: 'root'
})
export class UsuarioService {
  private _loadingService = inject(LoadingService);
  private readonly API_URI: string = API_URI + 'app';

  usuario = signal<Usuario>({
    id: '',
    id_perfil: 2, // USUARIO
    x_email: '',
    x_password: '',
    x_img_avatar: null,
    id_tipo_documento: null,
    x_nro_documento: null,
    x_telefono_emergencia: null,
    x_nro_socio: null,
    d_fecha_creacion: new Date(),
    x_mp_code: null,
    x_mp_access_token: null,
    x_mp_refresh_token: null,
    d_mp_expires_in: null,
    b_activo: true
  });

  cliente = signal<Cliente>({
    id: '',
    x_nombre: null,
    x_apellido: null,
    x_telefono: null,
    x_img_dni_frente: null,
    x_img_dni_dorso: null,
    id_obra_social: null,
    x_obra_social_plan: null,
    x_img_credencial_obra_social: null
  });

  prestador = signal<Prestador>({
    id: '',
    x_nombre: null,
    x_apellido: null,
    x_telefono: null,
    x_img_dni_frente: null,
    x_img_dni_dorso: null,
    x_img_matricula_frente: null,
    x_img_matricula_dorso: null,
    b_reprogramacion_turno: false
  });

  comercio = signal<Comercio>({
    id: '',
    x_nombre_fantasia: null,
    x_razon_social: null,
    x_nro_habilitacion: null,
    x_responsable_comercial: null,
    b_mercadopago: false,
    b_envios: false,
    b_vende_medicamentos: true
  });
  
  ubicacion_default = new BehaviorSubject<Ubicacion>({x_domicilio: ''});

  obrasSociales: ObraSocial[] = [];
  tiposDocumentos = signal<TipoDocumento[]>([]);
  perfiles: Perfil[] = [];
  private  isLoadedGoogleMaps: boolean = false;
  asociadoCardio = signal<boolean>(false);
  asociadoCardioLoading = signal<boolean>(false);

  constructor(private http: HttpClient){
    this.llenarDatos();
    this.loadGoogleMaps();
  }

  private llenarDatos(){
    this.getObrasSociales().subscribe({
      next: (res) =>  {
        this.obrasSociales = res.map(os => ({
          ...os,
          b_activo: !!os.b_activo // convierte 1 → true y 0 → false
        }));
      },
      error: (e) => console.log("Error al obtener las Obras Sociales"),
      complete: () => console.log("Obras Sociales obtenidas"),
    });

    this.getTiposDocumentos().subscribe({
      next: (res) =>
        this.tiposDocumentos.set(res.map(td => ({ ...td, b_activo: !!td.b_activo }))),
      error: (e) => console.log("Error al obtener los Tipos de Documentos"),
      complete: () => console.log("Tipos de Documentos obtenidos"),
    });

    this.getPerfiles().subscribe({
      next: (res) =>  {
        this.perfiles = res.map(pe => ({
          ...pe,
          b_activo: !!pe.b_activo // convierte 1 → true y 0 → false
        }));
      },
      error: (e) => console.log("Error al obtener los Perfiles"),
      complete: () => console.log("Perfiles obtenidos"),
    });
  }

  checkAsociadoXDni(x_nro_documento: string) {
    const json = { numeroDni: x_nro_documento };
    // Llamada "silenciosa" (sin overlay global).
    return this.http.post<{ habilitado: boolean; empresa?: string }>(
      'https://api.cardio24hs.ar/api/servicios/getinfobydni',
      json
    ).pipe(retry(this.reintentarSoloFallasDeRed()));
  }

  checkAsociadoXNroSocio(x_nro_socio: string) {
    /*
    Busca por numero de socio del titular.
    Si es un aderente, deberia colocar como numero de socio, el del grupo, osea el titular
    */
    const json = { numeroSocio : x_nro_socio };
    // Llamada "silenciosa" (sin overlay global).
    return this.http.post<{ habilitado: boolean; empresa?: string; nombre?: string; dni?: string }>(
      'https://api.cardio24hs.ar/api/servicios/getinfobysocio',
      json
    ).pipe(retry(this.reintentarSoloFallasDeRed()));
  }

  // Reintenta la llamada a la API de Cardio24hs solo ante fallas de red/timeout
  // (status 0) o del servidor (5xx) — un 400 significa "no es socio" y
  // reintentarlo no cambiaría nada. Backoff simple: 800ms, 1600ms.
  private reintentarSoloFallasDeRed() {
    return {
      count: 2,
      delay: (error: any, retryCount: number) => {
        const status = error?.status;
        if (status === 0 || (status >= 500 && status < 600)) {
          return timer(800 * retryCount);
        }
        throw error;
      }
    };
  }

  desvincularMercadoPago(id_usuario: string): Observable<any> {
    return this.http.put<any>(
      `${this.API_URI}/usuarios/desvincularMercadoPago/${id_usuario}`,
      {}
    );
  }

  getUsuarios(b_activo?: boolean): Observable<Usuario[]>{
    const url = b_activo === undefined
      ? `${this.API_URI}/usuarios/buscar`
      : `${this.API_URI}/usuarios/buscar/${Number(b_activo)}`; // Comillas invertidas ALT + 96

    return this.http.get<Usuario[]>(url);
  }

  getUsuariosXPerfil(id_perfil: number): Observable<Usuario[]>{
    return this.http.get<Usuario[]>(`${this.API_URI}/usuarios/xPerfil/${id_perfil}`);
  }

  getClientes(b_activo?: boolean): Observable<Cliente[]>{
    const url = b_activo === undefined
      ? `${this.API_URI}/clientes/buscar`
      : `${this.API_URI}/clientes/buscar/${Number(b_activo)}`; // Comillas invertidas ALT + 96

    return this.http.get<Cliente[]>(url);
  }

  getPrestadores(b_activo?: boolean): Observable<Prestador[]>{
    const url = b_activo === undefined
      ? `${this.API_URI}/prestadores/buscar`
      : `${this.API_URI}/prestadores/buscar/${Number(b_activo)}`; // Comillas invertidas ALT + 96

    return this.http.get<Prestador[]>(url);
  }

  getPrestadoresValidos(b_activo?: boolean): Observable<Prestador[]>{
    const url = b_activo === undefined
      ? `${this.API_URI}/prestadores/buscarValidos`
      : `${this.API_URI}/prestadores/buscarValidos/${Number(b_activo)}`;

    return this.http.get<Prestador[]>(url);
  }

  getComercios(b_activo?: boolean): Observable<Comercio[]>{
    const url = b_activo === undefined
      ? `${this.API_URI}/comercios/buscar`
      : `${this.API_URI}/comercios/buscar/${Number(b_activo)}`;

    return this.http.get<Comercio[]>(url);
  }

  getUsuarioXEmail(x_email: string): Observable<Usuario>{
    return this.http.get<Usuario>(`${this.API_URI}/usuarios/xEmail/${x_email}`);
  }

  getUsuarioXId(id_usuario: string): Observable<Usuario | null>{
    return this.http
      .get<Usuario>(`${this.API_URI}/usuarios/xId/${id_usuario}`)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);   // no existe calificación => ok
          return throwError(() => err);               // cualquier otro error => que explote
        })
      );
  }

  getClienteXId(id_usuario: string): Observable<Cliente | null>{
    return this.http
      .get<Cliente>(`${this.API_URI}/clientes/xId/${id_usuario}`)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);   // no existe calificación => ok
          return throwError(() => err);               // cualquier otro error => que explote
        })
      );
  }

  getComercioXId(id_usuario: string): Observable<Comercio | null>{
    return this.http
      .get<Comercio>(`${this.API_URI}/comercios/xId/${id_usuario}`)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);   // no existe calificación => ok
          return throwError(() => err);               // cualquier otro error => que explote
        })
      );
  }

  getPrestadorXId(id_usuario: string): Observable<Prestador | null>{
    return this.http
      .get<Prestador>(`${this.API_URI}/prestadores/xId/${id_usuario}`)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);   // no existe calificación => ok
          return throwError(() => err);               // cualquier otro error => que explote
        })
      );
  }

  getUbicacion(id: number): Observable<Ubicacion | null>{
    return this.http
      .get<Ubicacion>(`${this.API_URI}/ubicaciones/xId/${id}`)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);   // no existe calificación => ok
          return throwError(() => err);               // cualquier otro error => que explote
        })
      );
  }

  getUbicaciones(id_usuario: string, b_activo?: boolean): Observable<Ubicacion[]>{
    const url = b_activo === undefined
      ? `${this.API_URI}/ubicaciones/xUsuario/${id_usuario}`
      : `${this.API_URI}/ubicaciones/xUsuario/${id_usuario}/${Number(b_activo)}`;

    return this.http.get<Ubicacion[]>(url);
  }

  getUbicacionDefault(id_usuario: string): Promise<void> {
    return new Promise((resolved, reject) => {
      this.getUbicaciones(id_usuario).subscribe({
        next: (res) =>  {
          var ubiTemp = res.find(u => u.b_default);
          this.ubicacion_default.next(ubiTemp ? ubiTemp : {});
          resolved();
        },
        error: (e) => {
          console.error("Error al obtener las ubicaciones", e);
          reject(e); // Rechazar solo en caso de error real
        },
        complete: () => console.log("Ubicaciones obtenidas"),
      });
    });
  }

  getHorario(id_usuario: string, b_activo?: boolean): Observable<Horario | null> {
    const url = b_activo === undefined
      ? `${this.API_URI}/horarios/xUsuario/${id_usuario}`
      : `${this.API_URI}/horarios/xUsuario/${id_usuario}/${Number(b_activo)}`;

    return this.http
      .get<Horario>(url)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);
          return throwError(() => err);
        })
      );
  }

  getProducto(id: number): Observable<Producto | null>{
    return this.http
      .get<Producto>(`${this.API_URI}/productos/xId/${id}`)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);   // no existe calificación => ok
          return throwError(() => err);               // cualquier otro error => que explote
        })
      );
  }

  getProductos(id_usuario: string, b_activo?: boolean): Observable<Producto[]>{
    const url = b_activo === undefined
      ? `${this.API_URI}/productos/xUsuario/${id_usuario}`
      : `${this.API_URI}/productos/xUsuario/${id_usuario}/${Number(b_activo)}`;

    return this.http.get<Producto[]>(url);
  }

  getServicio(id: number): Observable<Servicio | null>{
    return this.http
      .get<Servicio>(`${this.API_URI}/servicios/xId/${id}`)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);   // no existe calificación => ok
          return throwError(() => err);               // cualquier otro error => que explote
        })
      );
  }

  getServicios(id_usuario?: string, b_activo?: boolean): Observable<Servicio[]>{
    const params: any = {};
    // Agregar parámetros sólo si están definidos
    if (id_usuario) {
      params.id_usuario = id_usuario;
    }
    if (b_activo !== undefined) {
      params.b_activo = Number(b_activo);
    }
    return this.http.get<Servicio[]>(`${this.API_URI}/servicios/xUsuario/`, {params});
  }

  getTipoDocumento(id: number): Observable<TipoDocumento | null>{
    return this.http
      .get<TipoDocumento>(`${this.API_URI}/tiposDocumentos/xId/${id}`)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);   // no existe calificación => ok
          return throwError(() => err);               // cualquier otro error => que explote
        })
      );
  }

  private getTiposDocumentos(b_activo?: boolean): Observable<TipoDocumento[]> {
    const url = b_activo === undefined
      ? `${this.API_URI}/tiposDocumentos/buscar`
      : `${this.API_URI}/tiposDocumentos/buscar/${Number(b_activo)}`;

    return this.http.get<TipoDocumento[]>(url);
  }

  getEspecialidad(id: number): Observable<Especialidad | null>{
    return this.http
      .get<Especialidad>(`${this.API_URI}/especialidades/xId/${id}`)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);   // no existe calificación => ok
          return throwError(() => err);               // cualquier otro error => que explote
        })
      );
  }

  getEspecialidades(b_activo?: boolean): Observable<Especialidad[]>{
    const url = b_activo === undefined
      ? `${this.API_URI}/especialidades/buscar`
      : `${this.API_URI}/especialidades/buscar/${Number(b_activo)}`;

    return this.http.get<Especialidad[]>(url);
  }

  getObraSocial(id: number): Observable<ObraSocial | null>{
    return this.http
      .get<ObraSocial>(`${this.API_URI}/obrasSociales/xId/${id}`)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);   // no existe calificación => ok
          return throwError(() => err);               // cualquier otro error => que explote
        })
      );
  }

  private getObrasSociales(b_activo?: boolean): Observable<ObraSocial[]>{
    const url = b_activo === undefined
      ? `${this.API_URI}/obrasSociales/buscar`
      : `${this.API_URI}/obrasSociales/buscar/${Number(b_activo)}`;

    return this.http.get<ObraSocial[]>(url);
  }

  getPrestacion(id: number): Observable<Prestacion | null>{
    return this.http
      .get<Prestacion>(`${this.API_URI}/prestaciones/xId/${id}`)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);   // no existe calificación => ok
          return throwError(() => err);               // cualquier otro error => que explote
        })
      );
  }

  getPrestaciones(b_activo?: boolean): Observable<Prestacion[]>{
    const url = b_activo === undefined
      ? `${this.API_URI}/prestaciones/buscar`
      : `${this.API_URI}/prestaciones/buscar/${Number(b_activo)}`;

    return this.http.get<Prestacion[]>(url);
  }

  getPrestacionesXEspecialidad(id_especialidad: number): Observable<Prestacion[]>{
    return this.http.get<Prestacion[]>(`${this.API_URI}/prestaciones/xEspecialidad/${id_especialidad}`);
  }

  private getPerfiles(): Observable<Perfil[]>{
    return this.http.get<Perfil[]>(`${this.API_URI}/perfiles/buscar`);
  }

  getMercadoPagoUrlAutorizacion(id_usuario: string): Observable<string>{
    return this.http.get<string>(`${this.API_URI}/mercadoPago/getUrlAuthorizacion/${id_usuario}`);
  }

  getDashboard(d_fecha_desde: Date, d_fecha_hasta: Date): Observable<any>{
    const params = new HttpParams()
        .set('d_fecha_desde', d_fecha_desde.toISOString().split('T')[0]) // Formato YYYY-MM-DD
        .set('d_fecha_hasta', d_fecha_hasta.toISOString().split('T')[0]); // Formato YYYY-MM-DD
    return this.http.get<any>(`${this.API_URI}/usuarios/dashboard`, { params });
  }

  updateMercadoPagoRefreshToken(id_usuario: string, x_mp_refresh_token: string): Observable<string>{
    let data = {
      id_usuario: id_usuario,
      x_mp_refresh_token: x_mp_refresh_token
    }
    return this.http.put<string>(`${this.API_URI}/mercadoPago/refreshToken`, data);
  }

  createUsuario(usuario: Usuario): Observable<void>{
    return this.http.post<void>(`${this.API_URI}/usuarios`, usuario);
  }

  createUsuarioFirebase(usuario: Usuario): Observable<void>{
    return this.http.post<void>(`${this.API_URI}/usuarios/createAdmin`, usuario);
  }

  createCliente(cliente: Cliente): Observable<void>{
    return this.http.post<void>(`${this.API_URI}/clientes`, cliente);
  }

  createComercio(comercio: Comercio): Observable<void>{
    return this.http.post<void>(`${this.API_URI}/comercios`, comercio);
  }

  createPrestador(prestador: Prestador): Observable<void>{
    return this.http.post<void>(`${this.API_URI}/prestadores`, prestador);
  }

  createUbicacion(ubicacion: Ubicacion): Observable<void>{
    return this.http.post<void>(`${this.API_URI}/ubicaciones`, ubicacion);
  }

  createHorario(horario: Horario): Observable<void>{
    return this.http.post<void>(`${this.API_URI}/horarios`, horario);
  }

  createProducto(producto: Producto): Observable<any>{
    return this.http.post<any>(`${this.API_URI}/productos`, producto);
  }

  createServicio(servicio: Servicio): Observable<any>{
    delete servicio.descripcionEspecialidad;
    delete servicio.descripcionPrestacion;
    return this.http.post<any>(`${this.API_URI}/servicios`, servicio);
  }

  createTipoDocumento(tipoDocumento: TipoDocumento): Observable<any>{
    return this.http.post<any>(`${this.API_URI}/tiposDocumentos`, tipoDocumento);
  }

  createEspecialidad(especialidad: Especialidad): Observable<any>{
    return this.http.post<any>(`${this.API_URI}/especialidades`, especialidad);
  }

  createObraSocial(obraSocial: ObraSocial): Observable<any>{
    return this.http.post<any>(`${this.API_URI}/obrasSociales`, obraSocial);
  }

  createPrestacion(prestacion: Prestacion): Observable<any>{
    return this.http.post<any>(`${this.API_URI}/prestaciones`, prestacion);
  }

  deleteUsuario(x_email: string): Observable<void>{
    return this.http.delete<void>(`${this.API_URI}/usuarios/${x_email}`);
  }

  deleteAccount(x_email: string): Observable<void>{
    return this.http.delete<void>(`${this.API_URI}/usuarios/deleteAccount/${x_email}?x_app=libresalud`);
  }

  updateUsuario(usuario: Usuario): Observable<void>{
    delete usuario.d_fecha_creacion;
    return this.http.put<void>(`${this.API_URI}/usuarios/xId/${usuario.id}`, usuario);
  }

  updateUsuarioFirebase(usuario: Usuario): Observable<void>{
    delete usuario.d_fecha_creacion;
    return this.http.put<void>(`${this.API_URI}/usuarios/updateAdmin`, usuario);
  }

  updateCliente(cliente: Cliente): Observable<void>{
    return this.http.put<void>(`${this.API_URI}/clientes/${cliente.id}`, cliente);
  }

  updateComercio(comercio: Comercio): Observable<void>{
    return this.http.put<void>(`${this.API_URI}/comercios/${comercio.id}`, comercio);
  }

  updatePrestador(prestador: Prestador): Observable<void>{
    return this.http.put<void>(`${this.API_URI}/prestadores/${prestador.id}`, prestador);
  }

  updateUbicacion(ubicacion: Ubicacion): Observable<void>{
    return this.http.put<void>(`${this.API_URI}/ubicaciones/${ubicacion.id}`, ubicacion);
  }

  updateHorario(horario: Horario): Observable<void>{
    return this.http.put<void>(`${this.API_URI}/horarios/${horario.id}`, horario);
  }

  updateProducto(producto: Producto): Observable<void>{
    return this.http.put<void>(`${this.API_URI}/productos/${producto.id}`, producto);
  }

  updateServicio(servicio: Servicio): Observable<void>{
    delete servicio.descripcionEspecialidad;
    delete servicio.descripcionPrestacion;
    return this.http.put<void>(`${this.API_URI}/servicios/${servicio.id}`, servicio);
  }

  updateTipoDocumento(tipoDocumento: TipoDocumento): Observable<void>{
    return this.http.put<void>(`${this.API_URI}/tiposDocumentos/${tipoDocumento.id}`, tipoDocumento);
  }

  updateEspecialidad(especialidad: Especialidad): Observable<void>{
    return this.http.put<void>(`${this.API_URI}/especialidades/${especialidad.id}`, especialidad);
  }

  updateObraSocial(obraSocial: ObraSocial): Observable<void>{
    return this.http.put<void>(`${this.API_URI}/obrasSociales/${obraSocial.id}`, obraSocial);
  }

  updatePrestacion(prestacion: Prestacion): Observable<void>{
    return this.http.put<void>(`${this.API_URI}/prestaciones/${prestacion.id}`, prestacion);
  }

  updateUsuarioImgAvatar(x_img_avatar: File, x_email: string): Observable<any>{
    const formularioDeDatos = new FormData();
    formularioDeDatos.append('img_avatar', x_img_avatar);
    return this.http.post(`${this.API_URI}/usuarios/avatar/${x_email}`, formularioDeDatos);
  }

  updateClienteImgDniFrente(x_img_dni_frente: File, id_cliente: string): Observable<any>{
    const formularioDeDatos = new FormData();
    formularioDeDatos.append('img_dni_frente', x_img_dni_frente);
    return this.http.post(`${this.API_URI}/clientes/dniFrente/${id_cliente}`, formularioDeDatos);
  }

  updateClienteImgDniDorso(x_img_dni_dorso: File, id_cliente: string): Observable<any>{
    const formularioDeDatos = new FormData();
    formularioDeDatos.append('img_dni_dorso', x_img_dni_dorso);
    return this.http.post(`${this.API_URI}/clientes/dniDorso/${id_cliente}`, formularioDeDatos);
  }

  deleteClienteImgDniFrente(id_cliente: string): Observable<any>{
    return this.http.delete(`${this.API_URI}/clientes/dniFrente/${id_cliente}`);
  }

  deleteClienteImgDniDorso(id_cliente: string): Observable<any>{
    return this.http.delete(`${this.API_URI}/clientes/dniDorso/${id_cliente}`);
  }

  updateClienteImgCredencialObraSocial(x_img_credencial_obra_social: File): Observable<any>{
    const formularioDeDatos = new FormData();
    formularioDeDatos.append('img_credencial_obra_social', x_img_credencial_obra_social);
    return this.http.post(`${this.API_URI}/clientes/credencialObrasocial/${this.usuario().id}`, formularioDeDatos);
  }

  deleteClienteImgCredencialObraSocial(): Observable<any>{
    return this.http.delete(`${this.API_URI}/clientes/credencialObrasocial/${this.usuario().id}`);
  }

  updatePrestadorImgDniFrente(x_img_dni_frente: File, id_prestador: string): Observable<any>{
    const formularioDeDatos = new FormData();
    formularioDeDatos.append('img_dni_frente', x_img_dni_frente);
    return this.http.post(`${this.API_URI}/prestadores/dniFrente/${id_prestador}`, formularioDeDatos);
  }

  updatePrestadorImgDniDorso(x_img_dni_dorso: File, id_prestador: string): Observable<any>{
    const formularioDeDatos = new FormData();
    formularioDeDatos.append('img_dni_dorso', x_img_dni_dorso);
    return this.http.post(`${this.API_URI}/prestadores/dniDorso/${id_prestador}`, formularioDeDatos);
  }

  updatePrestadorImgMatriculaFrente(x_img_matricula_frente: File, id_prestador: string): Observable<any>{
    const formularioDeDatos = new FormData();
    formularioDeDatos.append('img_matricula_frente', x_img_matricula_frente);
    return this.http.post(`${this.API_URI}/prestadores/matriculaFrente/${id_prestador}`, formularioDeDatos);
  }

  updatePrestadorImgMatriculaDorso(x_img_matricula_dorso: File, id_prestador: string): Observable<any>{
    const formularioDeDatos = new FormData();
    formularioDeDatos.append('img_matricula_dorso', x_img_matricula_dorso);
    return this.http.post(`${this.API_URI}/prestadores/matriculaDorso/${id_prestador}`, formularioDeDatos);
  }

  deletePrestadorImgDniFrente(id_prestador: string): Observable<any>{
    return this.http.delete(`${this.API_URI}/prestadores/dniFrente/${id_prestador}`);
  }

  deletePrestadorImgDniDorso(id_prestador: string): Observable<any>{
    return this.http.delete(`${this.API_URI}/prestadores/dniDorso/${id_prestador}`);
  }

  deletePrestadorImgMatriculaFrente(id_prestador: string): Observable<any>{
    return this.http.delete(`${this.API_URI}/prestadores/matriculaFrente/${id_prestador}`);
  }

  deletePrestadorImgMatriculaDorso(id_prestador: string): Observable<any>{
    return this.http.delete(`${this.API_URI}/prestadores/matriculaDorso/${id_prestador}`);
  }

  updateProductoImg(x_img_producto: File, id_producto: number): Observable<any>{
    const formularioDeDatos = new FormData();
    formularioDeDatos.append('img_producto', x_img_producto);
    return this.http.post(`${this.API_URI}/productos/img/${id_producto}`, formularioDeDatos);
  }

  deleteProductoImg(id_producto: number): Observable<any>{
    return this.http.delete(`${this.API_URI}/productos/img/${id_producto}`);
  }

  solicitudEspecialidadPrestacion(x_email: string, x_especialidad: string, x_prestacion: string): Observable<string>{
    let data = {
      x_email: x_email,
      x_especialidad: x_especialidad,
      x_prestacion: x_prestacion
    }
    return this.http.post<string>(`${this.API_URI}/usuarios/solicitudEspecialidadPrestacion`, data);
  }

  async refreshAsociadoCardio(): Promise<void> {
    const usuario = this.usuario();

    const dni = usuario?.x_nro_documento;
    const nroSocio = usuario?.x_nro_socio;

    let asociado = false;

    // Primero intentamos por número de socio
    if (nroSocio) {
      try {
        const res: any = await firstValueFrom(
          this.checkAsociadoXNroSocio(nroSocio)
        );

        asociado = !!res?.habilitado;
      } catch {}
    }

    // Si todavía no es socio, intentamos por DNI
    if (!asociado && dni) {
      try {
        const res: any = await firstValueFrom(
          this.checkAsociadoXDni(dni)
        );

        asociado = !!res?.habilitado;
      } catch {}
    }

    this.asociadoCardio.set(asociado);
  }

  // ============ Crear entidades por perfil (async/await) ============
  async createUsuarioXPerfil(): Promise<string> {
    const u = this.usuario();
    if (!u?.id) throw new Error('Usuario sin id');

    return await this._loadingService.run(async () => {
      switch (u.id_perfil) {
        case 1: // ADMIN => mismo flujo que CLIENTE
        case 2: { // CLIENTE
          const cli = await firstValueFrom(this.getClienteXId(u.id!));

          if(!cli){
            const nuevo: Cliente = { ...this.cliente(), id: u.id };
            await firstValueFrom(this.createCliente(nuevo));
            this.cliente.set(nuevo);
            return 'Cliente nuevo creado en BD';
          }

          this.cliente.set(cli);
          return 'Cliente existente';
        }
        case 3: { // PRESTADOR
          const pres = await firstValueFrom(this.getPrestadorXId(u.id!));

          if(!pres){
            const nuevo: Prestador = { ...this.prestador(), id: u.id };
            await firstValueFrom(this.createPrestador(nuevo));
            this.prestador.set(nuevo);
            return 'Prestador nuevo creado en BD';
          }

          this.prestador.set(pres);
          return 'Prestador existente';
        }
        case 4: { // COMERCIO
          const comer = await firstValueFrom(this.getComercioXId(u.id!));

          if(!comer){
            const nuevo: Prestador = { ...this.comercio(), id: u.id };
            await firstValueFrom(this.createComercio(nuevo));
            this.comercio.set(nuevo);
            return 'Comercio nuevo creado en BD';
          }

          this.comercio.set(comer);
          return 'Comercio existente';
        }
        default:
          return 'Perfil no reconocido';
      }
    });
  }

  loadGoogleMaps(): Promise<void> {
    if (this.isLoadedGoogleMaps) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY_GOOGLE_MAPS}&loading=async&v=beta&libraries=maps,marker`;
      //script.async = true;
      script.defer = true;
      script.setAttribute('loading', 'async');
      script.onload = () => {
        this.isLoadedGoogleMaps = true;
        resolve();
      };
      script.onerror = (error) => reject(error);
      document.head.appendChild(script);
    });
  }

  async getCurrentPosition(): Promise<GeolocationPosition> {
    if (!IS_NATIVE) {
      return new Promise<GeolocationPosition>((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation no está disponible en este navegador.'));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve(pos),
          (err) => reject(err),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      });
    }

    // Nativo: verificar y pedir permisos, luego usar el resultado para decidir
    let perm = await Geolocation.checkPermissions();

    if (perm.location !== 'granted') {
      perm = await Geolocation.requestPermissions({ permissions: ['location'] });
    }

    if (perm.location !== 'granted') {
      // Lanzamos un error con code=1 igual que la API web para que el handler sea uniforme
      throw Object.assign(new Error('Permiso de ubicación denegado.'), { code: 1 });
    }

    try {
      // Primer intento: alta precisión (GPS)
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 15000 });
      return this.normalizeCapacitorPosition(pos);
    } catch {
      // Fallback: baja precisión (red/WiFi) — más rápido, funciona en interiores
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 10000 });
      return this.normalizeCapacitorPosition(pos);
    }
  }

  private normalizeCapacitorPosition(pos: any): GeolocationPosition {
    return {
      coords: {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        altitude: null,
        altitudeAccuracy: null,
        heading: null,
        speed: null,
      },
      timestamp: Date.now(),
    } as GeolocationPosition;
  }

  clearData(){
    const usuarioNew: Usuario = {
      id: '',
      id_perfil: 2, // USUARIO
      x_email: '',
      x_password: '',
      x_img_avatar: null,
      id_tipo_documento: null,
      x_nro_documento: null,
      x_telefono_emergencia: null,
      x_nro_socio: null,
      d_fecha_creacion: new Date(),
      x_mp_code: null,
      x_mp_access_token: null,
      x_mp_refresh_token: null,
      d_mp_expires_in: null,
      b_activo: true
    };
  
    const clienteNew: Cliente = {
      id: '',
      x_nombre: null,
      x_apellido: null,
      x_telefono: null,
      x_img_dni_frente: null,
      x_img_dni_dorso: null,
      id_obra_social: null,
      x_obra_social_plan: null,
      x_img_credencial_obra_social: null
    }
  
    const prestadorNew: Prestador = {
      id: '',
      x_nombre: null,
      x_apellido: null,
      x_telefono: null,
      x_img_dni_frente: null,
      x_img_dni_dorso: null,
      x_img_matricula_frente: null,
      x_img_matricula_dorso: null,
      b_reprogramacion_turno: false
    }
  
    const comercioNew: Comercio = {
      id: '',
      x_nombre_fantasia: null,
      x_razon_social: null,
      x_nro_habilitacion: null,
      x_responsable_comercial: null,
      b_mercadopago: false,
      b_envios: false,
      b_vende_medicamentos: true
    }

    this.usuario.set(usuarioNew);
    this.cliente.set(clienteNew);
    this.prestador.set(prestadorNew);
    this.comercio.set(comercioNew);
    this.ubicacion_default.next({});
  }
}
